import React, { useEffect, useRef, useState } from "react";
import FilerobotImageEditor, { TABS } from "react-filerobot-image-editor";
import {
  Modal,
  Box,
  CircularProgress,
  Typography,
  GlobalStyles,
} from "@mui/material";
import { editorTheme } from "./editorTheme";
import { useTranslation } from "react-i18next";
import { imageDesignState, savedImageData } from "./types";
import { messages } from "../../../locales/messages";

/**
 * Props interface for the ImageEditorModal component.
 *
 * @property open - Controls whether the modal is visible
 * @property onClose - Callback invoked when the modal should close
 * @property imageSrc - URL of the image to edit
 * @property onSave - Callback invoked when user saves the edited image, receives image data and design state
 * @property zIndex - Z-index value for the modal overlay (default: 1300)
 */
interface ImageEditorModalProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  onSave: (
    editedImageObject: savedImageData,
    designState: imageDesignState,
  ) => void;
  /**
   * Z-index for the modal
   * @default 1300
   */
  zIndex?: number;
}

/**
 * Result type returned by the getCurrentImgData function.
 *
 * @property imageData - The saved image data object
 * @property designState - The current design state of the editor
 * @property hideLoadingSpinner - Optional function to hide loading spinner
 */
type GetCurrentImgDataResult = {
  imageData: savedImageData;
  designState: imageDesignState;
  hideLoadingSpinner?: () => void;
};

/**
 * Function type for retrieving current image data from the editor.
 *
 * @param imageFileInfo - Optional metadata about the image file
 * @param pixelRatio - Pixel ratio for image export, or false to use default
 * @param keepLoadingSpinnerShown - Whether to keep loading spinner visible
 * @returns Image data result object or Promise resolving to result
 */
type GetCurrentImgDataFn = (
  imageFileInfo?: Record<string, unknown>,
  pixelRatio?: number | false,
  keepLoadingSpinnerShown?: boolean,
) => GetCurrentImgDataResult | Promise<GetCurrentImgDataResult>;

/**
 * Modal component that provides image editing capabilities using Filerobot Image Editor.
 *
 * This component wraps the FilerobotImageEditor in a Material-UI modal, providing a full-featured
 * image editing experience with support for adjustments, annotations, filters, and more. The image
 * is preloaded to ensure it's fully available before the editor initializes. The modal handles
 * loading states, errors, and cross-origin image loading with proper CORS configuration.
 *
 * Key features:
 * - Image preloading with loading state
 * - CORS-enabled image loading
 * - Cache-busting for fresh image loading
 * - Responsive design for mobile and desktop
 * - Customized editor theme and localization support
 * - Multiple editing tabs (Adjust, Annotate, Finetune, Filters, Resize)
 *
 * @param props - Component properties
 * @param props.open - Whether the modal is currently visible
 * @param props.onClose - Callback to close the modal
 * @param props.imageSrc - URL of the image to edit
 * @param props.onSave - Callback when user saves edits, receives edited image and design state
 * @param props.zIndex - Z-index for modal stacking (default: 1300)
 */
export const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  open,
  onClose,
  imageSrc,
  onSave: handleSave,
  zIndex = 1300,
}) => {
  const { t, i18n } = useTranslation();
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getCurrentImgDataRef = useRef<GetCurrentImgDataFn | null>(null);
  const hasUnsavedChangesRef = useRef(false);

  useEffect(() => {
    if (!open) {
      if (document.body.dataset.imageEditorActive === "true") {
        delete document.body.dataset.imageEditorActive;
      }
      return;
    }

    const previousState = document.body.dataset.imageEditorActive;
    document.body.dataset.imageEditorActive = "true";

    return () => {
      if (previousState) {
        document.body.dataset.imageEditorActive = previousState;
      } else {
        delete document.body.dataset.imageEditorActive;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !imageSrc) {
      setImageElement(null);
      setIsLoading(true);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      setImageElement(img);
      setIsLoading(false);
    };

    img.onerror = (err) => {
      console.error("Error loading image:", err);
      setError("Failed to load image. Please try again.");
      setIsLoading(false);
    };

    const cacheBustedSrc = imageSrc.includes("?")
      ? `${imageSrc}&_t=${Date.now()}`
      : `${imageSrc}?_t=${Date.now()}`;
    img.src = cacheBustedSrc;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [open, imageSrc]);

  return (
    <>
      <GlobalStyles
        styles={{
          ".SfxModal-Wrapper": {
            zIndex: `${zIndex + 100} !important`,
          },
          ".FIE_crop-presets-menu": {
            zIndex: `${zIndex + 200} !important`,
          },
          "[data-id='FIE_crop-presets-menu']": {
            zIndex: `${zIndex + 200} !important`,
          },
          ".SfxPopper-root": {
            zIndex: `${zIndex + 200} !important`,
          },
          ".SfxPopper-wrapper": {
            zIndex: `${zIndex + 200} !important`,
          },
        }}
      />
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="image-editor-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex,
          p: { xs: 0, sm: 0 },
        }}
        disableScrollLock={false}
      >
        <Box
          sx={{
            width: { xs: "100dvw", sm: "95vw" },
            height: {
              xs: "100dvh",
              sm: "95vh",
            },
            backgroundColor: "white",
            borderRadius: { xs: 0, sm: "8px" },
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            zIndex: 1100,
          }}
          data-stop-global-enter="true"
        >
          {isLoading && (
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress sx={{ color: "#ff813a" }} />
              <Typography sx={{ mt: 2, color: "#666" }}>
                {t(messages.Loading())}
              </Typography>
            </Box>
          )}

          {error && (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography color="error" variant="h6">
                {error}
              </Typography>
              <Typography sx={{ mt: 2, color: "#666" }}>
                Image URL: {imageSrc}
              </Typography>
            </Box>
          )}

          {!isLoading && !error && imageElement && (
            <FilerobotImageEditor
              source={imageElement}
              theme={editorTheme}
              defaultSavedImageName="flyer-preview"
              defaultSavedImageType="png"
              getCurrentImgDataFnRef={getCurrentImgDataRef}
              closeAfterSave={false}
              disableSaveIfNoChanges={true}
              onModify={() => {
                hasUnsavedChangesRef.current = true;
              }}
              onBeforeSave={() => {
                return false;
              }}
              onSave={(editedImageObject, designState) => {
                handleSave(editedImageObject, designState);
                onClose();
              }}
              onClose={onClose}
              annotationsCommon={{
                fill: "#ff813a",
              }}
              Text={{ text: "Add text..." }}
              Rotate={{ angle: 90, componentType: "slider" }}
              Crop={{
                ratio: "custom",
                noPresets: true,
              }}
              tabsIds={[
                TABS.ADJUST,
                TABS.ANNOTATE,
                TABS.FINETUNE,
                TABS.FILTERS,
                TABS.RESIZE,
              ]}
              defaultTabId={TABS.ADJUST}
              savingPixelRatio={4}
              previewPixelRatio={window.devicePixelRatio || 1}
              defaultToolId="Rotate"
              useZoomPresetsMenu={true}
              language={i18n.language}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};
